/*
 * Copyright (C) 2026 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import {GlobalEnv} from '@canvas/global/env/GlobalEnv'
import doFetchApi from '@canvas/do-fetch-api-effect'

declare global {
  interface Window {
    // undefined until _head.html.erb publishes it from PRE_FUNCTIONAL_COOKIE_CONSENT
    CANVAS_FUNCTIONAL_COOKIE_CONSENT_STATE: boolean | null | undefined
    CANVAS_COOKIE_CONSENT_STATE: boolean | null
  }
}
declare const ENV: GlobalEnv

interface CookieType {
  name: string
  value: string
  domain?: string
  path?: string
  expires?: Date
  secure?: boolean
  sameSite?: 'Strict' | 'Lax' | 'None'
}

export const oneTrustFunctionalCookieClass: string = 'C0003'
const isDevEnv: boolean = typeof ENV !== 'undefined' && ENV.RAILS_ENVIRONMENT === 'development'
const debuglog: (msg: string) => void = isDevEnv
  ? (message: string) => {
      // eslint-disable-next-line no-console
      console.log(message)
    }
  : () => {}

// Cookies Rails sets that fall under C0003. Not reachable from JS, so
// clearFunctionalCookies can't do anything with them -- the actual expiry happens
// in UsersController#destroy_functional_cookies, which we call on revocation. The
// list documents the scope and keeps the exact-name branch covered.
const serverSetFunctionalCookies: readonly string[] = [
  // Set httponly and scoped to path=/login (login/otp_controller.rb), so it never
  // appears in document.cookie here. TODO: revoking consent expires it, but a
  // later login with "remember me" checked will set it again -- Rails doesn't
  // consult consent at login time yet.
  'canvas_otp_remember_me',
]
const clientSetFunctionalCookies: readonly string[] = [
  'unsupported_browser_dismissed',
  'ui-tabs-*',
  'kaltura_analytic_tracker',
  'k5_observed_user_for_*',
]

const waitingCookieSets: CookieType[] = []

// Functional cookies default to allowed, and are only held back once something
// is actually there to gate them:
//   false -> a consent mechanism reported no C0003. Withhold.
//   null  -> OneTrust is on the page but hasn't reported yet. Withhold, since it
//            is about to tell us, and a premature write we then clear is worse
//            than a write deferred by a few milliseconds.
//   true / undefined -> write. undefined means the state was never published at
//            all (no js_env on the page), so there is no consent mechanism in
//            play and no one whose preference we could be violating.
const functionalConsentGranted = (): boolean => {
  const state = window.CANVAS_FUNCTIONAL_COOKIE_CONSENT_STATE
  return state !== false && state !== null
}

const writeCookie = (cookie: CookieType): void => {
  const attributes = [
    `${encodeURIComponent(cookie.name)}=${encodeURIComponent(cookie.value)}`,
    `path=${cookie.path || '/'}`,
  ]
  // Only send Domain when the caller asked for it. A cookie written with an
  // explicit Domain is a separate entry from the host-only cookie the browser
  // stores without one, so defaulting to location.hostname would both widen the
  // scope of every cookie routed through here and leave the host-only copies
  // behind when we clear.
  if (cookie.domain) attributes.push(`domain=${cookie.domain}`)
  if (cookie.expires) attributes.push(`expires=${cookie.expires.toUTCString()}`)
  if (cookie.secure) attributes.push('secure')
  if (cookie.sameSite) attributes.push(`SameSite=${cookie.sameSite}`)
  document.cookie = attributes.join('; ')
}

// Every domain a cookie on this host could legitimately be scoped to: the host
// itself, then each parent domain up to (not including) the registrable pair.
const clearableDomains = (): string[] => {
  const host = window.location.hostname
  const labels = host.split('.')
  const domains = [host]
  for (let i = 1; i <= labels.length - 2; i++) {
    domains.push(`.${labels.slice(i).join('.')}`)
  }
  return domains
}

// document.cookie exposes only names and values, never the Domain a cookie was
// written with, so expire every scope the name could be living under.
const expireCookie = (name: string): void => {
  const expired = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
  document.cookie = expired
  for (const domain of clearableDomains()) {
    document.cookie = `${expired}; domain=${domain}`
  }
}

export const setFunctionalCookie = (
  name: string,
  value: string,
  domain: string = '',
  path: string = '/',
  expires?: Date,
  secure?: boolean,
  sameSite?: 'Strict' | 'Lax' | 'None',
): void => {
  const cookie: CookieType = {name, value, domain, path, expires, secure, sameSite}
  if (functionalConsentGranted()) {
    writeCookie(cookie)
    debuglog(`Functional cookie set: ${cookie.name}=${cookie.value}`)
  } else {
    waitingCookieSets.push(cookie)
    debuglog(`Functional cookie queued: ${cookie.name}=${cookie.value}`)
  }
}

export const clearFunctionalCookies = (): void => {
  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const cookieName = cookie.split('=')[0].trim()
    if (
      serverSetFunctionalCookies.includes(cookieName) ||
      clientSetFunctionalCookies.some(pattern => {
        const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\\\*/g, '.*')
        return new RegExp(`^${escaped}$`).test(cookieName)
      })
    ) {
      expireCookie(cookieName)
      debuglog(`Functional cookie cleared: ${cookieName}`)
    }
  }
}

export const processWaitingCookieSets = (): void => {
  if (functionalConsentGranted()) {
    waitingCookieSets.forEach(cookie => {
      writeCookie(cookie)
      debuglog(`Functional cookie set from queue: ${cookie.name}=${cookie.value}`)
    })
    waitingCookieSets.length = 0
  }
}

export const FUNCTIONAL_COOKIES_PATH = '/users/self/functional_cookies'
// Marks that the server has already been asked to expire its functional cookies,
// so a revocation carried across page loads doesn't re-send on every one. Only set
// on success, so a failed request is retried on the next page load.
const SERVER_CLEARED_KEY = 'canvas_functional_cookies_cleared'

// sessionStorage throws rather than no-ops in some embedded contexts, and a
// consent decision must not be derailed by that.
const sessionFlag = {
  get(): boolean {
    try {
      return window.sessionStorage.getItem(SERVER_CLEARED_KEY) === 'true'
    } catch {
      return false
    }
  },
  set(value: boolean): void {
    try {
      if (value) {
        window.sessionStorage.setItem(SERVER_CLEARED_KEY, 'true')
      } else {
        window.sessionStorage.removeItem(SERVER_CLEARED_KEY)
      }
    } catch {
      // no-op
    }
  },
}

// Asks Rails to expire the functional cookies it issues. They can't be cleared
// from here: canvas_otp_remember_me is httponly and scoped to /login, so it isn't
// even visible in document.cookie. Deliberately not awaited -- revoking consent
// must not block on the network, and a failure is retried on the next page load.
export const requestServerFunctionalCookieClear = (): Promise<void> =>
  doFetchApi({path: FUNCTIONAL_COOKIES_PATH, method: 'DELETE'})
    .then(() => {
      sessionFlag.set(true)
      debuglog('Server-set functional cookies cleared.')
    })
    .catch(() => {
      debuglog('Failed to clear server-set functional cookies; will retry on next load.')
    })

// OneTrust can dispatch OneTrustGroupsUpdated before this bundle finishes
// loading. In that case the inline handler in _head.html.erb has already recorded
// the consent state on window, but nothing cleared the cookies already on disk,
// and our own listener won't fire again until the next consent change. Call this
// once at boot to catch up. Only an explicit revocation clears -- a null state
// means OneTrust still hasn't reported.
export const reconcileFunctionalCookies = (): void => {
  if (window.CANVAS_FUNCTIONAL_COOKIE_CONSENT_STATE === false) {
    debuglog('Functional cookie consent was already revoked before boot.')
    clearFunctionalCookies()
    // The revocation itself already asked the server once. Re-send only if that
    // never landed, so this doesn't cost a request on every page load.
    if (!sessionFlag.get()) {
      requestServerFunctionalCookieClear()
    }
  }
}

// Applies a OneTrust consent update: grants functional consent and flushes any
// queued cookies when C0003 is present, otherwise revokes and clears them.
export const handleOneTrustGroupsUpdated = (detail: string[] | string): void => {
  if (detail.includes(oneTrustFunctionalCookieClass)) {
    window.CANVAS_FUNCTIONAL_COOKIE_CONSENT_STATE = true
    debuglog('Functional cookie consent granted.')
    // Re-arm the server clear, so a later revocation asks the server again rather
    // than reading a stale marker from earlier in this tab session.
    sessionFlag.set(false)
    processWaitingCookieSets()
  } else {
    window.CANVAS_FUNCTIONAL_COOKIE_CONSENT_STATE = false
    debuglog('Functional cookie consent revoked.')
    clearFunctionalCookies()
    requestServerFunctionalCookieClear()
  }
}
